import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ChevronLeft, Loader2 } from 'lucide-react';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import teamAvatar from '@/assets/cardsimp-team-avatar.png';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useChatStore } from '@/store/chatStore';
import { ChatComposer } from './ChatComposer';
import type { ChatMessage } from '@/hooks/useTeamConversation';

type Subject = {
	value: string;
	label: string;
	emoji: string;
	echo: string;
	reply: string;
};

const SUBJECTS: Subject[] = [
	{
		value: 'Question / Support',
		label: 'Support',
		emoji: '💬',
		echo: 'I need some help.',
		reply:
			"We're here to help. Please describe your question or issue below — we usually reply within 24–48 hours. Include screenshots and your CardSimp handle if you have one.",
	},
	{
		value: 'Bug Report',
		label: 'Bug',
		emoji: '🐛',
		echo: 'I found a bug.',
		reply:
			"Sorry about that. Please include what you were doing, what happened instead, and your device/browser. Screenshots help a ton.",
	},
	{
		value: 'Feedback',
		label: 'Feedback',
		emoji: '✨',
		echo: 'I have feedback.',
		reply: 'Your input shapes what we build next. Let us know what you want to see.',
	},
	{
		value: 'Feature Request',
		label: 'Feature idea',
		emoji: '💡',
		echo: 'I have a feature idea.',
		reply: 'Describe an idea and the problem it would solve for you.',
	},
	{
		value: 'Partnership',
		label: 'Partnership',
		emoji: '🤝',
		echo: "Let's partner up.",
		reply: "Send a brief note of what you're imagining, and we'll contact you.",
	},
	{
		value: 'Business',
		label: 'Business',
		emoji: '📈',
		echo: 'Business inquiry.',
		reply:
			"For vendor, press, or commercial inquiries, send a brief note and we'll route you to the right contact.",
	},
];

const greetingFor = () => 'Hello.';

interface ChatThreadProps {
	onBack: () => void;
	conversationId: string | null;
	messages: ChatMessage[];
	onMessageSent: (msg: ChatMessage, conversationId: string) => void;
}

// --- Bubble primitives ---
function TeamAvatar({ size = 24 }: { size?: number }) {
	return (
		<div
			className="rounded-full overflow-hidden bg-[hsl(var(--active-gold)/0.15)] shrink-0"
			style={{
				width: size,
				height: size,
				boxShadow: `0 0 0 1px hsl(var(--active-gold) / ${size >= 32 ? 0.3 : 0.25})`,
			}}
		>
			<img
				src={teamAvatar}
				alt=""
				className="w-full h-full object-cover scale-[1.35] translate-y-[6%]"
				draggable={false}
			/>
		</div>
	);
}

function TeamBubble({ children }: { children: React.ReactNode }) {
	return (
		<div className="flex items-end gap-2 animate-fade-in">
			<TeamAvatar size={24} />
			<div className="max-w-[82%] rounded-2xl rounded-bl-md bg-white/[0.05] px-4 py-2.5 text-[14px] text-foreground leading-snug whitespace-pre-wrap break-words">
				{children}
			</div>
		</div>
	);
}

function TypingBubble() {
	return (
		<div className="flex items-end gap-2 animate-fade-in">
			<TeamAvatar size={24} />
			<div className="rounded-2xl rounded-bl-md bg-white/[0.05] px-4 py-3 inline-flex items-center gap-1">
				<span className="h-1.5 w-1.5 rounded-full bg-foreground/50 animate-[contact-typing_1s_infinite]" />
				<span className="h-1.5 w-1.5 rounded-full bg-foreground/50 animate-[contact-typing_1s_infinite] [animation-delay:150ms]" />
				<span className="h-1.5 w-1.5 rounded-full bg-foreground/50 animate-[contact-typing_1s_infinite] [animation-delay:300ms]" />
			</div>
		</div>
	);
}

function UserGlyph({ avatarUrl, username }: { avatarUrl?: string | null; username?: string | null }) {
	if (avatarUrl) {
		return (
			<img
				src={avatarUrl}
				alt={username ?? 'You'}
				className="h-6 w-6 shrink-0 rounded-full object-cover border border-white/[0.10]"
			/>
		);
	}
	return (
		<div
			className="h-6 w-6 shrink-0 rounded-full bg-white/[0.08] border border-white/[0.10] grid place-items-center"
			aria-hidden
		>
			<svg
				viewBox="0 0 24 24"
				className="h-3.5 w-3.5 text-foreground/50"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				<circle cx="12" cy="8" r="4" />
				<path d="M5 21v-1a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v1" />
			</svg>
		</div>
	);
}

function UserEchoBubble({
	children,
	avatarUrl,
	username,
}: {
	children: React.ReactNode;
	avatarUrl?: string | null;
	username?: string | null;
}) {
	return (
		<div className="flex items-end gap-2 justify-end animate-fade-in">
			<div className="max-w-[85%] rounded-2xl rounded-br-md bg-white/[0.08] px-4 py-2.5 text-[14px] text-foreground leading-snug">
				{children}
			</div>
			<UserGlyph avatarUrl={avatarUrl} username={username} />
		</div>
	);
}

export const ChatThread = ({ onBack, conversationId, messages, onMessageSent }: ChatThreadProps) => {
	const { user } = useAuth();
	const { profile } = useProfile();
	const avatarUrl = profile?.avatar_url ?? null;
	const username = profile?.username ?? null;
	const pendingSubject = useChatStore((s) => s.pendingSubject);
	const clearPendingSubject = useChatStore((s) => s.clearPendingSubject);
	// No subject is pre-selected: arriving in the thread must not fake a
	// "I need some help." message the user never sent.
	const [subject, setSubject] = useState<string>('');
	const [isSending, setIsSending] = useState(false);
	const [echoShown, setEchoShown] = useState<string | null>(null);
	const [autoReplyTyping, setAutoReplyTyping] = useState(false);
	const [autoReplyShown, setAutoReplyShown] = useState<string | null>(null);
	const [postSendShown, setPostSendShown] = useState(false);
	const postSendTimerRef = useRef<number | null>(null);
	const scrollRef = useRef<HTMLDivElement>(null);
	const greeting = useMemo(() => greetingFor(), []);

	const activeSubject = useMemo(
		() => SUBJECTS.find((s) => s.value === subject),
		[subject]
	);
	const hasHistory = messages.length > 0;

	// Honor a subject pre-selected by an entry point (e.g. /contact?subject=Partnership).
	useEffect(() => {
		if (!pendingSubject) return;
		const match = SUBJECTS.find((s) => s.value === pendingSubject);
		if (match) setSubject(match.value);
		clearPendingSubject();
	}, [pendingSubject, clearPendingSubject]);

	// Subject ritual: echo (220ms) → typing (720ms) → reply (1500ms).
	// Only runs while there's no real history — once the user sends, the thread
	// is "live" and the canned reply has done its job.
	useEffect(() => {
		if (hasHistory) {
			setEchoShown(null);
			setAutoReplyTyping(false);
			setAutoReplyShown(null);
			return;
		}
		if (!subject) return;
		if (autoReplyShown === subject) return;
		setEchoShown(null);
		setAutoReplyTyping(false);
		setAutoReplyShown(null);
		const tEcho = window.setTimeout(() => setEchoShown(subject), 220);
		const tType = window.setTimeout(() => setAutoReplyTyping(true), 720);
		const tReply = window.setTimeout(() => {
			setAutoReplyTyping(false);
			setAutoReplyShown(subject);
		}, 1500);
		return () => {
			clearTimeout(tEcho);
			clearTimeout(tType);
			clearTimeout(tReply);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [subject, hasHistory]);

	// Reset post-send confirmation when subject changes or new history arrives.
	// Auto-dismiss after 5 seconds so it doesn't linger forever.
	useEffect(() => {
		setPostSendShown(false);
		if (postSendTimerRef.current) {
			window.clearTimeout(postSendTimerRef.current);
			postSendTimerRef.current = null;
		}
	}, [subject, messages.length]);

	useEffect(() => {
		if (!postSendShown) return;
		postSendTimerRef.current = window.setTimeout(() => setPostSendShown(false), 5000);
		return () => {
			if (postSendTimerRef.current) {
				window.clearTimeout(postSendTimerRef.current);
				postSendTimerRef.current = null;
			}
		};
	}, [postSendShown]);

	// Auto-scroll on every visual beat.
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
	}, [
		messages.length,
		subject,
		echoShown,
		autoReplyTyping,
		autoReplyShown,
		postSendShown,
		isSending,
	]);

	const handleSend = async ({ body, attachmentPaths }: { body: string; attachmentPaths: string[] }) => {
		if (!user) {
			toast.error('Please sign in to chat');
			return;
		}
		setIsSending(true);
		try {
			const { data, error } = await supabase.functions.invoke('send-contact-email', {
				body: {
					name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'User',
					email: user.email,
					subject: subject || 'Question / Support',
					message: body,
					attachmentPaths,
					pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
					userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
					userId: user.id,
					useChatThread: true,
				},
			});
			if (error) {
				if ((error as any).context?.status === 429) {
					toast.error('Too many messages', { description: 'Please try again later.' });
					return;
				}
				throw error;
			}
			const resp = data as { conversationId?: string; messageId?: string } | null;
			if (resp?.conversationId && resp?.messageId) {
				onMessageSent(
					{
						id: resp.messageId,
						conversation_id: resp.conversationId,
						sender_id: user.id,
						kind: 'user',
						body,
						attachments: attachmentPaths,
						created_at: new Date().toISOString(),
					},
					resp.conversationId
				);
			}
			setPostSendShown(true);
		} catch (err) {
			console.error('[chat] send failed', err);
			toast.error('Could not send', { description: 'Please try again in a moment.' });
		} finally {
			setIsSending(false);
		}
	};

	return (
		<div className="flex flex-col h-full">
			{/* Header */}
			<div className="px-3 pt-2.5 pb-2.5 flex items-center gap-2 shrink-0 border-b border-white/[0.05]">
				<button
					type="button"
					onClick={onBack}
					aria-label="Back to inbox"
					className="h-8 w-8 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-white/[0.05] transition-colors -ml-1"
				>
					<ChevronLeft className="h-5 w-5" />
				</button>
				<div className="relative shrink-0">
					<TeamAvatar size={36} />
					<span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-success ring-2 ring-chrome" />
				</div>
				<div className="flex-1 min-w-0">
					<div className="text-[13px] font-semibold text-foreground leading-tight">CardSimp Team</div>
					<div className="text-[11px] text-muted-foreground leading-tight truncate">
						contact@cardsimp.com
					</div>
				</div>
			</div>

			{/* Conversation */}
			<div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
				{/* Greeting */}
				<TeamBubble>{greeting} How can we help you today?</TeamBubble>

				{/* Subject ritual — only before real history exists */}
				{!hasHistory && echoShown && activeSubject && (
					<UserEchoBubble key={`echo-${echoShown}`} avatarUrl={avatarUrl} username={username}>{activeSubject.echo}</UserEchoBubble>
				)}
				{!hasHistory && autoReplyTyping && <TypingBubble key={`typing-${subject}`} />}
				{!hasHistory && !autoReplyTyping && autoReplyShown && activeSubject && (
					<TeamBubble key={`reply-${autoReplyShown}`}>{activeSubject.reply}</TeamBubble>
				)}

				{/* History */}
				{messages.map((m) => {
					const mine = m.sender_id && user && m.sender_id === user.id;
					if (m.kind === 'system') {
						return (
							<div key={m.id} className="text-center text-[11px] text-muted-foreground/70 py-1">
								{m.body}
							</div>
						);
					}
					if (mine) {
						return (
							<div key={m.id} className="flex items-end gap-2 justify-end">
								<div className="max-w-[82%] rounded-2xl rounded-br-md bg-primary text-primary-foreground px-4 py-2.5 text-[14px] leading-snug whitespace-pre-wrap break-words">
									{m.body}
								</div>
								<UserGlyph avatarUrl={avatarUrl} username={username} />
							</div>
						);
					}
					return (
						<TeamBubble key={m.id}>{m.body}</TeamBubble>
					);
				})}

				{/* Post-send confirmation */}
				{postSendShown && (
					<TeamBubble key="post-send">
						Got it ✨ We'll reply within 24–48 hours.
					</TeamBubble>
				)}

				{isSending && (
					<div className="flex items-center gap-2 text-muted-foreground text-[12px] pl-8">
						<Loader2 className="h-3 w-3 animate-spin" />
						Sending…
					</div>
				)}
			</div>

			{/* Subject pills — stay mounted; collapse to selected + Back once history exists */}
			<div className="px-3 pt-3 pb-2 shrink-0 border-t border-white/[0.04]">
				<div className="flex flex-wrap gap-2 w-full min-w-0">
					{hasHistory && activeSubject && (
						<button
							type="button"
							onClick={() => {
								// Reset subject so the full topic grid re-appears and the
								// next message can be re-targeted cleanly.
								setSubject('');
								setEchoShown(null);
								setAutoReplyTyping(false);
								setAutoReplyShown(null);
							}}
							aria-label="Change topic"
							className="inline-flex items-center gap-1 h-9 pl-2.5 pr-3 rounded-full text-[12.5px] font-medium transition-all whitespace-nowrap active:scale-95 border bg-white/[0.05] border-white/[0.08] text-foreground hover:bg-white/[0.08]"
						>
							<ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.5} />
							Topics
						</button>
					)}
					{SUBJECTS.filter((s) => !hasHistory || !subject || s.value === subject).map((s) => {
						const active = subject === s.value;
						return (
							<button
								key={s.value}
								type="button"
								onClick={() => setSubject(s.value)}
								className={cn(
									'inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-full text-[13px] font-medium transition-all min-w-0',
									hasHistory
										? ''
										: 'flex-1 basis-[calc(50%-0.25rem)] sm:basis-[calc(33.333%-0.375rem)]',
									active
										? 'bg-primary text-primary-foreground shadow-sm shadow-primary/20'
										: 'bg-white/[0.07] text-foreground hover:bg-white/[0.10] border border-white/[0.12]'
								)}
							>
								<span className="shrink-0">{s.emoji}</span>
								<span className="truncate">{s.label}</span>
							</button>
						);
					})}
				</div>
			</div>

			{/* Composer */}
			<ChatComposer
				disabled={!user || isSending}
				placeholder={
					hasHistory
						? 'Type a message…'
						: activeSubject
						? `Message us about ${activeSubject.label.toLowerCase()}…`
						: 'Type a message…'
				}
				allowAttachments
				onSend={handleSend}
			/>
		</div>
	);
};

export default ChatThread;
